<template>
  <div class="prevent-overflow">
    <div>
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <form @submit.prevent="() => {}">
          <div v-if="showSpeech" class="mb-4">
            <SubTitleView>{{ $t("registerPage.etablissement-search-title") }}</SubTitleView>
            <H3TitleView class="mb-2"
              >{{ $t("registerPage.etablissement-search-subtitle") }}
            </H3TitleView>
            <!-- eslint-disable vue/no-v-html -->
            <div>{{ $t("registerPage.etablissement-search-step-1") }}</div>
            <div>{{ $t("registerPage.etablissement-search-step-2") }}</div>
            <div>{{ $t("registerPage.etablissement-search-step-3") }}</div>
            <div class="mb-4">{{ $t("registerPage.etablissement-search-step-4") }}</div>
            <div v-html="$t('registerPage.etablissement-info')"></div>
          </div>
          <!-- eslint-enable vue/no-v-html -->

          <section class="etablissement-search">
            <b-field class="etablissement-search-field" grouped :label="inputLabel">
              <b-input
                v-model="etablissementSearch"
                :placeholder="$t('registerPage.entrer-etablissement')"
                class="etablissement-search-input"
                :message="$t('registerPage.chercher-etablissement-precision')"
                @input="resetSearch"
                @keyup.enter="handleSubmit(search)"
              ></b-input>
              <p class="control">
                <b-button type="is-primary" rounded @click="handleSubmit(search)">
                  {{ $t("EtablissementSelectionView.chercher") }}
                </b-button>
              </p>
            </b-field>
          </section>
        </form>
      </ValidationObserver>
      <div v-if="isLoading">
        <b-skeleton width="40%" animated></b-skeleton>
        <b-skeleton width="80%" animated></b-skeleton>
        <br />
        <br />
      </div>
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <b-field v-if="etablissementsFromApi.length !== 0" :class="{ 'vertical-radios': true }">
          <b-radio
            v-for="eta in etablissementsFromApi"
            :key="eta.codeEtablissement"
            v-model="selectedEtablissement"
            :class="[
              'etablissement-line',
              {
                selected:
                  selectedEtablissement &&
                  selectedEtablissement.codeEtablissement === eta.codeEtablissement,
              },
            ]"
            :native-value="eta"
            type="is-light"
          >
            <div>
              <span class="etablissement-nom">{{ eta.nom + " " }}</span>
              <span class="etablissement-infos"
                >{{ $t("registerPage.uai") }} {{ eta.codeEtablissement }}</span
              >
            </div>
            <div class="etablissement-infos">
              {{ `${eta.rue} ${eta.codePostal} ${eta.ville}` }}
            </div>
          </b-radio>
        </b-field>
        <b-button v-if="nbResultats > 0 && hasNext" type="is-text" @click="seeNext">
          {{ $t("EtablissementSelectionView.see10Next") }}
        </b-button>

        <div v-if="notFound && !isLoading">
          {{ $t("EtablissementSelectionView.etablissement-non-trouve") }}
        </div>

        <div v-if="showBottomButtons" class="buttons">
          <b-button v-if="enableBack" type="is-dark" @click="goBack">
            {{ goBackLabel }}
          </b-button>
          <b-button
            type="is-primary"
            :disabled="!selectedEtablissement || !selectedEtablissement.codeEtablissement"
            @click="handleSubmit(submit)"
          >
            <span>
              {{ buttonLabel }}
            </span>
          </b-button>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { EtablissementDTO } from "@/model/bean/GeneratedDTOs"
import SubTitleView from "@/views/commons/SubTitleView.vue"
import H3TitleView from "@/views/commons/H3TitleView.vue"
import { t } from "@/utils/i18n-utils"
import { BButton, BField, BInput, BRadio, BSkeleton } from "buefy"

export default defineComponent({
  name: "EtablissementSelectionView",
  components: {
    ValidationObserver,
    SubTitleView,
    H3TitleView,
    BButton,
    BInput,
    BField,
    BRadio,
    BSkeleton,
  },
  props: {
    modelValue: {
      type: Object as () => EtablissementDTO,
      default: undefined,
    },
    enableBack: {
      type: Boolean,
      default: true,
    },
    buttonLabel: {
      type: String,
      default: "",
    },
    showSpeech: {
      type: Boolean,
      default: true,
    },
    inputLabel: {
      type: String,
      default: () => t("registerPage.chercher-etablissement"),
    },
    showBottomButtons: {
      type: Boolean,
      default: true,
    },
    goBackLabel: {
      type: String,
      default: () => t("registerPage.precedent"),
    },
  },

  emits: ["update:modelValue", "etablissement-selected", "go-back"],
  data() {
    return {
      organismeService: OrganismeService.INSTANCE,
      lastScheduledValidationTime: new Date().getTime(),
      etablissementSearch: "",
      etablissementsFromApi: [] as EtablissementDTO[],
      notFound: false,
      selectedEtablissement: undefined as unknown as EtablissementDTO,
      isLoading: false,
      nbResultats: 0,
      hasNext: true,
    }
  },
  watch: {
    selectedEtablissement(): void {
      if (this.selectedEtablissement && this.selectedEtablissement.codeEtablissement) {
        this.etablissementSearch = this.selectedEtablissement.nom
        this.$emit("etablissement-selected", this.selectedEtablissement)
      }
    },
  },
  mounted(): void {
    if (this.modelValue) {
      this.etablissementSearch = this.modelValue.nom || ""
    }
  },
  methods: {
    setNotFound(notFound: boolean): void {
      this.notFound = notFound
    },

    async search(): Promise<void> {
      try {
        console.debug(
          "[EtablissementSelectionView] Search method called with: ",
          this.etablissementSearch,
        )
        this.isLoading = true

        const etablissements = await this.organismeService.getEtablissementsFromAPI(
          this.etablissementSearch.toUpperCase(),
          this.nbResultats,
        )
        console.debug(
          "[EtablissementSelectionView] Search completed, results count: ",
          etablissements.length,
        )
        this.setNotFound(etablissements.length === 0)
        if (etablissements.length !== 0) {
          this.etablissementsFromApi = this.etablissementsFromApi.concat(etablissements)
          this.nbResultats += etablissements.length
        }
        this.hasNext = etablissements.length >= 10
      } catch (error) {
        console.error("[EtablissementSelectionView] Search error: ", error)
      }
      this.isLoading = false
    },

    seeNext(): void {
      this.search()
    },

    resetSearch(): void {
      this.etablissementsFromApi = []
      this.nbResultats = 0
      this.hasNext = true
    },

    submit(): void {
      this.$emit("update:modelValue", this.selectedEtablissement)
    },

    goBack(): void {
      console.debug("[EtablissementSelectionView] goBack method called")
      this.$emit("go-back", this.selectedEtablissement)
    },
  },
})
</script>

<style lang="scss" scoped>
.buttons {
  justify-content: space-between;
}
</style>

<style lang="scss">
@import "@/styles/variables.scss";

.EtablissementSelectionView-field {
  margin-bottom: $input-field-margin-bot;
}

.prevent-overflow {
  padding-bottom: 200px; // FIXME LK 19/11/20 : Better way to handle dropdown overflow ?
}

.etablissement-search {
  display: flex;
  margin-bottom: 20px;

  .etablissement-search-field {
    flex-grow: 1;
  }

  .etablissement-search-input {
    flex-grow: 1;
  }
}

.field.vertical-radios {
  display: flex;
  flex-direction: column;
}

.b-radio.radio.etablissement-line input[type="radio"] + .check.is-light {
  display: none;
}

.b-radio.etablissement-line {
  display: flex;
  justify-content: flex-start;
  height: 55px;
  align-content: center;
  line-height: 1.5;
  border: 1px solid #dbdbdb;

  &.selected {
    background-color: #d5d5d5;
    border-color: transparent;
    color: rgba(0, 0, 0, 0.7);
  }

  &:hover {
    border-color: #363636;
  }
}

.etablissement-infos {
  color: $dark;
  font-size: 12px;
}
</style>
